// src/shell/Nav.jsx
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { NAV, NO_SUBNAV_PATHS, COMMAND_TABS, COMMAND_PATHS } from './nav';
import { T } from '@components/theme';
import { useDisasterMode } from '@hooks/useDisasterMode';
import { useLangStore } from '@store/lang';

// ── Sub nav (tab strip under the header) ─────────────────────────
const TOOL_GROUPS = [
  { id: 'tools',   label: 'Emergency Tools', tabs: ['shelters','firstaid','gobag','binder','contacts'] },
  { id: 'prepare', label: 'Preparedness',    tabs: ['readiness','accessibility'] },
  { id: 'comms',   label: 'Communications',  tabs: ['share','hotspots','radio'] },
];

export function SubNav({ mode, situationLevel = 'normal' }) {
  const { lang } = useLangStore();
  const TAB_LABELS = { 'Situation': lang==='es'?'Situación':'Situation', 'Alerts': lang==='es'?'Alertas':'Alerts', 'Find Help': lang==='es'?'Encontrar Ayuda':'Find Help', 'Tools': lang==='es'?'Herramientas':'Tools', 'Map': lang==='es'?'Mapa':'Map', 'Evacuate': lang==='es'?'Evacuar':'Evacuate' };
  const disaster  = useDisasterMode();
  const navigate  = useNavigate();
  const location  = useLocation();
  const modeData  = NAV.find((m) => m.id === mode);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (NO_SUBNAV_PATHS.includes(location.pathname)) return null;

  // Command paths (Command/Map/Travel) always show COMMAND_TABS regardless of bottom nav mode
  const isCommandPath = COMMAND_PATHS.has(location.pathname);

  if (!modeData && !isCommandPath) return null;

  // During disaster: show emergency-relevant tabs from ALL nav modes
  const DISASTER_PATHS = new Set(['/feed','/news','/map','/evac','/traffic','/contacts','/shelters','/radio']);
  const allTabs = isCommandPath
    ? COMMAND_TABS
    : disaster.active
    ? NAV.flatMap(m => m.tabs).filter(t => DISASTER_PATHS.has(t.path))
    : (modeData?.tabs ?? []);

  // Split into primary (no group) and grouped tabs
  const primaryTabs = allTabs.filter(t => !t.group);
  const groupedTabs = allTabs.filter(t => t.group);
  const activeIsGrouped = groupedTabs.some(t => location.pathname === t.path);

  return (
    <>
      <div style={{
        display: 'flex', background: T.card,
        borderBottom: `1px solid ${T.bdr}`,
        flexShrink: 0, overflowX: 'auto', scrollbarWidth: 'none',
      }}>
        {primaryTabs.map((tab) => {
          const active = location.pathname === tab.path;
          return (
            <button key={tab.id} onClick={() => { navigate(tab.path); setDrawerOpen(false); }} style={{
              flex: 1, padding: '6px 4px', border: 'none',
              borderBottom: `2px solid ${active ? modeData.color : 'transparent'}`,
              background: 'none', fontSize: 10,
              fontWeight: active ? 900 : 600,
              color: active ? modeData.color : T.dim,
              cursor: 'pointer', fontFamily: T.font,
              minWidth: 60, transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}>
              <div style={{ fontSize: 14, marginBottom: 2 }}>{tab.emoji}</div>
              {TAB_LABELS[tab.label] ?? tab.label}
            </button>
          );
        })}

        {/* More button — shows when there are grouped tabs */}
        {groupedTabs.length > 0 && (
          <button onClick={() => setDrawerOpen(v => !v)} style={{
            padding: '6px 10px', border: 'none',
            borderBottom: `2px solid ${activeIsGrouped || drawerOpen ? modeData.color : 'transparent'}`,
            background: 'none', fontSize: 10,
            fontWeight: activeIsGrouped || drawerOpen ? 900 : 600,
            color: activeIsGrouped || drawerOpen ? modeData.color : T.dim,
            cursor: 'pointer', fontFamily: T.font,
            minWidth: 54, whiteSpace: 'nowrap',
          }}>
            <div style={{ fontSize: 14, marginBottom: 2 }}>
              {activeIsGrouped
                ? allTabs.find(t => location.pathname === t.path)?.emoji ?? '⋯'
                : '⋯'}
            </div>
            {activeIsGrouped
              ? allTabs.find(t => location.pathname === t.path)?.label ?? 'More'
              : drawerOpen ? 'Close' : 'More ▾'}
          </button>
        )}
      </div>

      {/* Grouped drawer */}
      {drawerOpen && (
        <div style={{
          background: T.card, borderBottom: `1px solid ${T.bdr}`,
          flexShrink: 0, padding: '8px 14px 12px',
        }}>
          {TOOL_GROUPS.map(group => {
            const tabs = groupedTabs.filter(t => group.tabs.includes(t.id));
            if (tabs.length === 0) return null;
            return (
              <div key={group.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: T.dim,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                  {group.label}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {tabs.map(tab => {
                    const active = location.pathname === tab.path;
                    return (
                      <button key={tab.id}
                        onClick={() => { navigate(tab.path); setDrawerOpen(false); }}
                        style={{
                          padding: '7px 12px', borderRadius: 8,
                          border: `1.5px solid ${active ? modeData.color : T.bdr}`,
                          background: active ? modeData.color + '15' : T.bg2,
                          color: active ? modeData.color : T.text,
                          fontSize: 11, fontWeight: active ? 800 : 600,
                          cursor: 'pointer', fontFamily: T.font,
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                        <span style={{ fontSize: 14 }}>{tab.emoji}</span>
                        {TAB_LABELS[tab.label] ?? tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Bottom nav (mode switcher) ────────────────────────────────────
export function BottomNav({ mode, setMode }) {
  const navigate = useNavigate();
  const { lang } = useLangStore();
  const NAV_LABELS = {
    HOME:             lang==='es' ? 'INICIO'      : 'HOME',
    'MY NEIGHBORHOOD':lang==='es' ? 'MI VECINDARIO':'MY NEIGHBORHOOD',
    RESOURCES:        lang==='es' ? 'RECURSOS'    : 'RESOURCES',
    MORE:             lang==='es' ? 'MÁS'         : 'MORE',
  };
  const { active: disasterActive } = useDisasterMode();
  const visibleModes = disasterActive
    ? NAV.filter(m => ['home','neighborhood','resources'].includes(m.id))
    : NAV;

  const handleMode = (m) => {
    setMode(m.id);
    navigate(m.defaultPath);
  };

  return (
    <nav aria-label="Main navigation" style={{
      display: 'flex', background: T.card,
      borderTop: `1px solid ${T.bdr}`,
      flexShrink: 0,
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {visibleModes.map((m) => {
        const active = mode === m.id;
        return (
          <button
            key={m.id}
            onClick={() => handleMode(m)}
        aria-label={`Navigate to ${m.label}`}
        aria-current={mode === m.id ? 'page' : undefined}
            style={{
              flex: 1, padding: '10px 4px 8px', border: 'none',
              background: 'none', cursor: 'pointer', fontFamily: T.font,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            }}
          >
            <span style={{ fontSize: 22 }}>{m.emoji}</span>
            <span style={{
              fontSize: 9, fontWeight: active ? 900 : 600,
              color: active ? m.color : T.dim,
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>
              {NAV_LABELS[m.label] ?? m.label}
            </span>
            {active && (
              <div style={{ width: 24, height: 3, borderRadius: 2, background: m.color }} />
            )}
          </button>
        );
      })}

    </nav>
  );
}
